const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'source', 'track', 'wbr',
]);

export type StreamingHtmlStage = 'empty' | 'styling' | 'building' | 'interactive';

export interface StreamingHtmlPreviewData {
  title?: string;
  html: string;
  css?: string;
  js?: string;
  minHeight: number;
  maxHeight?: number;
  /** Length of the raw (pre-sanitization) HTML content — used to decide loader vs iframe */
  contentLength: number;
  /** Which phase of streaming we're in — drives loader label */
  stage: StreamingHtmlStage;
}

function isFinitePositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Make mid-stream HTML safe to render inside an iframe without crashing.
 * - Drops a trailing partial tag like `<div class="`
 * - Truncates unclosed <script> / <style> blocks (would otherwise hang parsing)
 * - Auto-closes open block tags in reverse order
 */
export function sanitizePartialHtmlForPreview(html: string): string {
  let safe = html.trim();
  if (!safe) return '';

  const lastOpen = safe.lastIndexOf('<');
  const lastClose = safe.lastIndexOf('>');
  if (lastOpen > lastClose) {
    safe = safe.slice(0, lastOpen).trimEnd();
  }

  const scriptOpens = Array.from(safe.matchAll(/<script\b[^>]*>/gi));
  const scriptCloses = Array.from(safe.matchAll(/<\/script\s*>/gi));
  if (scriptOpens.length > scriptCloses.length) {
    const firstUnpaired = scriptOpens[scriptCloses.length];
    if (firstUnpaired?.index !== undefined) {
      safe = safe.slice(0, firstUnpaired.index).trimEnd();
    }
  }

  const styleOpens = Array.from(safe.matchAll(/<style\b[^>]*>/gi));
  const styleCloses = Array.from(safe.matchAll(/<\/style\s*>/gi));
  if (styleOpens.length > styleCloses.length) {
    const firstUnpaired = styleOpens[styleCloses.length];
    if (firstUnpaired?.index !== undefined) {
      safe = safe.slice(0, firstUnpaired.index).trimEnd();
    }
  }

  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)(?:\s[^>]*)?\/?>/g;
  const stack: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = tagPattern.exec(safe)) !== null) {
    const tag = m[1].toLowerCase();
    const full = m[0];
    if (VOID_TAGS.has(tag) || full.endsWith('/>')) continue;
    if (full.startsWith('</')) {
      const idx = stack.lastIndexOf(tag);
      if (idx !== -1) stack.splice(idx, 1);
    } else {
      stack.push(tag);
    }
  }
  while (stack.length > 0) {
    safe += `</${stack.pop()}>`;
  }

  return safe;
}

function computeStage(
  css: string | undefined,
  html: string,
  js: string | undefined,
): StreamingHtmlStage {
  const hasCss = !!(css && css.trim().length > 0);
  const hasHtml = html.trim().length > 0;
  const hasJs = !!(js && js.trim().length > 0);
  if (hasJs) return 'interactive';
  if (hasHtml) return 'building';
  if (hasCss) return 'styling';
  return 'empty';
}

export function getStreamingHtmlPreview(
  input: Record<string, unknown> | undefined,
): StreamingHtmlPreviewData | null {
  if (!input) return null;

  const rawHtml = typeof input.html === 'string' ? input.html : '';
  const contentLength = rawHtml.trim().length;
  const sanitized = sanitizePartialHtmlForPreview(rawHtml);

  const title = typeof input.title === 'string' && input.title.trim()
    ? input.title.trim()
    : undefined;
  const css = typeof input.css === 'string' ? input.css : undefined;
  const js = typeof input.js === 'string' ? input.js : undefined;
  const minHeight = isFinitePositiveNumber(input.minHeight) ? input.minHeight : 260;
  const maxHeight = isFinitePositiveNumber(input.maxHeight) ? input.maxHeight : undefined;

  return {
    title,
    html: sanitized,
    css,
    js,
    minHeight,
    maxHeight,
    contentLength,
    stage: computeStage(css, rawHtml, js),
  };
}
